import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  Building, 
  Calendar, 
  ArrowLeft, 
  RefreshCw,
  User,
  Briefcase,
  MessageSquare,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  LogOut
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardContent = ({ onLogout }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('All');
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const services = [
    'All',
    'UGC Creation',
    'Social Media Management',
    'Online Presence',
    'Brand Building',
    'Social Media Marketing',
    'Website Development',
    'App Development',
    'Other',
  ];

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('dashboard_token');
      const response = await axios.get(`${API}/inquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data || [];
      const sorted = data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setInquiries(sorted);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      if (error.response && error.response.status === 401) {
        toast.error('Session expired. Please sign in again.');
        sessionStorage.removeItem('dashboard_auth');
        sessionStorage.removeItem('dashboard_user');
        sessionStorage.removeItem('dashboard_token');
        if (typeof onLogout === 'function') onLogout();
      } else if (error.response && error.response.status !== 404) {
        toast.error('Failed to load inquiries');
      }
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredInquiries = inquiries.filter((inquiry) => {
    const matchesSearch = 
      inquiry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterService === 'All' || inquiry.service === filterService;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-[#F4F4F3]">
      {/* Header */}
      <header className="bg-[#1C1917] text-[#F4F4F3] py-6 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a 
              href="/"
              className="flex items-center gap-2 text-[#F4F4F3]/70 hover:text-[#F4F4F3] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Website</span>
            </a>
            <div className="h-6 w-px bg-[#F4F4F3]/20" />
            <h1 
              className="text-2xl font-bold tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Inquiries Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchInquiries}
              disabled={loading}
              className="flex items-center gap-2 bg-[#AA94FF] text-[#F4F4F3] px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#CC5003] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 bg-[#F4F4F3]/10 text-[#F4F4F3] px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#F4F4F3]/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-[#1C1917]/5"
          >
            <p className="text-sm text-[#78716C] mb-1">Total Inquiries</p>
            <p className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {inquiries.length}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-[#1C1917]/5"
          >
            <p className="text-sm text-[#78716C] mb-1">This Week</p>
            <p className="text-3xl font-bold text-[#AA94FF]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {inquiries.filter(i => {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return new Date(i.created_at) > weekAgo;
              }).length}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-[#1C1917]/5"
          >
            <p className="text-sm text-[#78716C] mb-1">Email Sent</p>
            <p className="text-3xl font-bold text-green-600" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {inquiries.filter(i => i.email_sent).length}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-[#1C1917]/5"
          >
            <p className="text-sm text-[#78716C] mb-1">Top Service</p>
            <p className="text-lg font-bold text-[#1C1917] truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {inquiries.length > 0 
                ? Object.entries(
                    inquiries.reduce((acc, i) => {
                      acc[i.service] = (acc[i.service] || 0) + 1;
                      return acc;
                    }, {})
                  ).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'
                : 'N/A'
              }
            </p>
          </motion.div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white rounded-full border border-[#AA94FF]/20 focus:border-[#AA94FF] focus:outline-none transition-colors"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="pl-12 pr-8 py-3 bg-white rounded-full border border-[#AA94FF]/20 focus:border-[#AA94FF] focus:outline-none transition-colors appearance-none cursor-pointer"
            >
              {services.map((service) => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Inquiries List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#1C1917]/5">
                <RefreshCw className="w-8 h-8 text-[#AA94FF] animate-spin mx-auto mb-4" />
                <p className="text-[#78716C]">Loading inquiries...</p>
              </div>
            ) : filteredInquiries.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#1C1917]/5">
                <MessageSquare className="w-12 h-12 text-[#78716C]/30 mx-auto mb-4" />
                <p className="text-[#78716C]">No inquiries found</p>
              </div>
            ) : (
              filteredInquiries.map((inquiry, index) => (
                <motion.div
                  key={inquiry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedInquiry(inquiry)}
                  className={`bg-white rounded-2xl p-6 border cursor-pointer transition-all hover:shadow-lg ${
                    selectedInquiry?.id === inquiry.id 
                      ? 'border-[#AA94FF] shadow-lg' 
                      : 'border-[#1C1917]/5 hover:border-[#AA94FF]/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#E7E5E4] flex items-center justify-center">
                        <User className="w-6 h-6 text-[#AA94FF]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                          {inquiry.name}
                        </h3>
                        <p className="text-sm text-[#78716C]">{inquiry.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {inquiry.email_sent ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Emailed
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                          <XCircle className="w-3 h-3" />
                          Not sent
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-[#78716C] mb-3">
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {inquiry.phone || 'No phone'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {inquiry.service || 'Not specified'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#78716C] flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(inquiry.created_at)}
                    </span>
                    {inquiry.company && (
                      <span className="text-xs text-[#78716C] flex items-center gap-1">
                        <Building className="w-3 h-3" />
                        {inquiry.company}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 border border-[#1C1917]/5 sticky top-8">
              {selectedInquiry ? (
                <>
                  <div className="flex items-center gap-3 mb-6 pb-6 border-b border-[#1C1917]/10">
                    <div className="w-16 h-16 rounded-full bg-[#E7E5E4] flex items-center justify-center">
                      <User className="w-8 h-8 text-[#AA94FF]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {selectedInquiry.name}
                      </h3>
                      <p className="text-sm text-[#78716C]">
                        {formatDate(selectedInquiry.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-[#78716C] uppercase tracking-wider">Email</label>
                      <a 
                        href={`mailto:${selectedInquiry.email}`}
                        className="flex items-center gap-2 mt-1 text-[#AA94FF] hover:underline"
                      >
                        <Mail className="w-4 h-4" />
                        {selectedInquiry.email}
                      </a>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-[#78716C] uppercase tracking-wider">Phone</label>
                      <a 
                        href={`tel:${selectedInquiry.phone}`}
                        className="flex items-center gap-2 mt-1 text-[#AA94FF] hover:underline"
                      >
                        <Phone className="w-4 h-4" />
                        {selectedInquiry.phone || 'Not provided'}
                      </a>
                    </div>

                    {selectedInquiry.company && (
                      <div>
                        <label className="text-xs font-semibold text-[#78716C] uppercase tracking-wider">Company</label>
                        <p className="flex items-center gap-2 mt-1 text-[#1C1917]">
                          <Building className="w-4 h-4 text-[#78716C]" />
                          {selectedInquiry.company}
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="text-xs font-semibold text-[#78716C] uppercase tracking-wider">Service Interested</label>
                      <p className="mt-1 text-[#1C1917] bg-[#E7E5E4] inline-block px-3 py-1 rounded-full text-sm font-medium">
                        {selectedInquiry.service || 'Not specified'}
                      </p>
                    </div>

                    {selectedInquiry.message && (
                      <div>
                        <label className="text-xs font-semibold text-[#78716C] uppercase tracking-wider">Message</label>
                        <p className="mt-2 text-[#1C1917] bg-[#F4F4F3] p-4 rounded-xl text-sm leading-relaxed">
                          {selectedInquiry.message}
                        </p>
                      </div>
                    )}

                    <div className="pt-4 border-t border-[#1C1917]/10 flex gap-2">
                      <a
                        href={`mailto:${selectedInquiry.email}`}
                        className="flex-1 bg-[#AA94FF] text-[#F4F4F3] py-3 rounded-full text-sm font-semibold text-center hover:bg-[#CC5003] transition-colors"
                      >
                        Reply via Email
                      </a>
                      <a
                        href={`tel:${selectedInquiry.phone}`}
                        className="flex-1 bg-[#1C1917] text-[#F4F4F3] py-3 rounded-full text-sm font-semibold text-center hover:bg-[#333] transition-colors"
                      >
                        Call
                      </a>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-[#78716C]/30 mx-auto mb-4" />
                  <p className="text-[#78716C]">Select an inquiry to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardContent;
